import { ensureGsap } from "@theme/vp-motion";
import { buildVehicleFilterUrl, cacheCosyForModel, clearVehicle, displayLabelForVehicle, formatKenteken, isCompleteKenteken, loadVehicle, lookupKenteken, saveVehicle, stripKenteken, uncacheCosyForModel, vehicleFilterBaseUrl, vehicleFilterClearUrl, vehicleFiltersNeedSync, vehicleFromModelSelect } from "@theme/kenteken-api";
import { collectionAllowsVehicleFilters, readModelCatalog } from "@theme/vp-model-catalog";
import { clearPlpVehiclePending, findDesktopFacetsFormComponent } from "@theme/facet-form-data";
import { isCosyImageUrl, isVehicleCosyImageUrl, managedCosyForVehicle } from "@theme/vp-model-cosy-kentekens";
import { dropdownFromVehicle, isKnownBrandAndModel } from "@theme/vp-vehicle-model-map";
import { computeCosyFit, isUsableCosyImage } from "@theme/vp-cosy-fit";
//#region assets/kenteken-panel.ts
/**
* Kenteken panel controller — cosy image, API lookup, model-select, filter redirect.
*
* Expects markup with `data-kenteken-panel` (hero, PLP sidebar, PDP, kenteken-config, menu).
* Hero / panel: lookup + cosy image as soon as the plate has 6 characters
* (`kenteken-input:complete`), and again on blur as a fallback. Zoeken
* navigates to PLP. Config / inline (PDP) / filter / menu: same complete + blur
* lookup. Config arrow then navigates to the chosen category PLP. Filter
* (PLP): applies merk + brand_and_model_v2 facets via Section Rendering (no
* full reload). Menu: navigates to the filtered PLP after a successful lookup.
* Image swaps via GSAP.
*
* Optional config chrome:
* - clear chip `[data-kenteken-clear]` + label `[data-kenteken-vehicle-label]`
* - category select `[data-kenteken-category]` (native or vp-category-select) for filtered navigation
*/
var COSY_FIT_BOX_ASPECT = 184 / 74;
var COSY_FIT_MAX_HEIGHT = 1.14;
var UNKNOWN_VEHICLE_MESSAGE = "Uw voertuig is niet bij ons bekend. Selecteer uw model of bekijk alle producten.";
var ERROR_MESSAGES = {
	invalid_kenteken: "Voer een geldig kenteken in (6 tekens).",
	not_found: UNKNOWN_VEHICLE_MESSAGE,
	unsupported_brand: UNKNOWN_VEHICLE_MESSAGE,
	no_model_match: UNKNOWN_VEHICLE_MESSAGE,
	missing_proxy: "Kentekenlookup is nog niet geconfigureerd.",
	upstream_error: "Voertuiglookup tijdelijk niet beschikbaar. Probeer het later opnieuw.",
	network: "Geen verbinding. Controleer uw internet en probeer opnieuw."
};
var REDUCED = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
/** @type {Promise<void> | null} */
var vehicleFilterRestoreSync = null;
/**
* @param {HTMLElement} root
* @returns {HTMLElement | null}
*/
function findFacetsFormForPanel(root) {
	const section = root.closest(".shopify-section");
	const sectionId = section?.querySelector("facets-form-component[section-id]")?.getAttribute("section-id");
	return (sectionId && section ? findDesktopFacetsFormComponent(section, sectionId) : null) || root.closest("facets-form-component") || section?.querySelector("facets-form-component") || document.querySelector("facets-form-component");
}
/**
* Run PLP vehicle filter restore sync once per navigation, after
* facets-form-component exposes updateFiltersByURL so #applyFilterUrl can use AJAX.
* @param {() => void} run
* @returns {Promise<void>}
*/
function scheduleVehicleFilterRestoreSync(run) {
	if (vehicleFilterRestoreSync) return vehicleFilterRestoreSync;
	vehicleFilterRestoreSync = (async () => {
		const deadline = Date.now() + 4e3;
		while (Date.now() < deadline) {
			const host = document.querySelector("facets-form-component");
			if (host) {
				await customElements.whenDefined("facets-form-component");
				if (typeof host.updateFiltersByURL === "function") break;
			}
			await new Promise((resolve) => setTimeout(resolve, 40));
		}
		await new Promise((resolve) => requestAnimationFrame(resolve));
		try {
			run();
		} catch {}
	})();
	return vehicleFilterRestoreSync;
}
/** @internal Test helper */
function resetVehicleFilterRestoreSyncForTests() {
	vehicleFilterRestoreSync = null;
}
var KentekenPanel = class {
	/**
	* @param {HTMLElement} root
	*/
	constructor(root) {
		this.root = root;
		this.form = root.querySelector("[data-kenteken-form]") || root.querySelector("form");
		this.variant = root.dataset.kentekenVariant || "panel";
		this.proxyUrl = root.dataset.proxyUrl || "";
		this.collectionUrl = root.dataset.collectionUrl || "/collections/all";
		this.defaultImageUrl = root.dataset.defaultImageUrl || "";
		/** @type {Record<string, string>} */
		this.modelImages = readModelImages(root);
		/** @type {Record<string, string>} */
		this.cosyImages = readCosyImages(root);
		this.cosyImage = root.querySelector("[data-kenteken-cosy-image]");
		this.placeholder = root.querySelector("[data-kenteken-placeholder]");
		this.statusEl = root.querySelector("[data-kenteken-status]");
		this.plateHidden = root.querySelector("[data-kenteken-value]");
		this.plateDisplay = root.querySelector("[data-kenteken-display]");
		this.modelSelect = root.querySelector("vp-model-select");
		this.modelCatalog = readModelCatalog(root);
		this.submitButton = root.querySelector("[type=\"submit\"]");
		this.clearButton = root.querySelector("[data-kenteken-clear]");
		this.vehicleLabelEl = root.querySelector("[data-kenteken-vehicle-label]");
		this.categorySelect = root.querySelector("[data-kenteken-category]");
		/** @type {import('@theme/kenteken-api').VpVehicle|null} */
		this.vehicle = null;
		/** @type {AbortController|null} */
		this.lookupController = null;
		/** @type {Promise<boolean>|null} */
		this._lookupPromise = null;
		/** @type {string} */
		this.lastLookedUp = "";
		/** @type {typeof window.gsap | null} */
		this.gsap = null;
		/** @type {gsap.core.Tween | gsap.core.Timeline | null} */
		this._cosyTween = null;
		/** @type {gsap.core.Tween | gsap.core.Timeline | null} */
		this._loadingTween = null;
		/** @type {gsap.core.Timeline | null} */
		this._invalidStrokeTween = null;
		/** True while a cosy URL is preloading / swapping in (blocks self-echo re-entry). */
		this._cosyInFlight = false;
		this.#bind();
		ensureGsap().then((gsap) => {
			this.gsap = gsap;
		});
		this.#restore();
		window.addEventListener("vp-vehicle:change", (event) => {
			this.#onExternalVehicleChange(event.detail?.vehicle ?? null);
		});
	}
	/**
	* Wait until kenteken controls are upgraded before syncing values.
	*/
	async #whenControlsReady() {
		await Promise.all([customElements.whenDefined("kenteken-input"), customElements.whenDefined("vp-model-select")]);
		this.plateHidden = this.root.querySelector("[data-kenteken-value]");
		this.plateDisplay = this.root.querySelector("[data-kenteken-display]");
		this.modelSelect = this.root.querySelector("vp-model-select");
		await new Promise((resolve) => requestAnimationFrame(resolve));
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	*/
	#syncControlsFromVehicle(vehicle) {
		this.#syncPlateFromVehicle(vehicle);
		this.#syncModelSelectFromVehicle(vehicle);
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	*/
	#syncPlateFromVehicle(vehicle) {
		const kenteken = vehicle?.kenteken && isCompleteKenteken(vehicle.kenteken) ? vehicle.kenteken : "";
		const plateHost = this.root.querySelector("kenteken-input");
		if (plateHost && typeof plateHost.setPlateValue === "function") plateHost.setPlateValue(kenteken);
		else if (this.plateDisplay && this.plateHidden) {
			if (kenteken) {
				this.plateHidden.value = kenteken;
				this.plateDisplay.value = formatKenteken(kenteken);
			} else {
				this.plateHidden.value = "";
				this.plateDisplay.value = "";
			}
		}
		this.lastLookedUp = kenteken;
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	* @returns {boolean}
	*/
	#controlsDifferFromVehicle(vehicle) {
		if (!vehicle) return false;
		if (stripKenteken(this.plateHidden?.value || this.plateDisplay?.value || "") !== (vehicle.kenteken && isCompleteKenteken(vehicle.kenteken) ? vehicle.kenteken : "")) return true;
		const brandAndModelInput = this.modelSelect?.querySelector("[data-vp-model-brand-and-model]");
		const modelValueInput = this.modelSelect?.querySelector("[data-vp-model-value]");
		const currentBrandAndModel = brandAndModelInput instanceof HTMLInputElement ? brandAndModelInput.value.trim() : "";
		const currentModel = modelValueInput instanceof HTMLInputElement ? modelValueInput.value.trim() : "";
		if (vehicle.source === "model-select" && Array.isArray(vehicle.brandAndModels) && vehicle.brandAndModels.length > 1 && !vehicle.chassisCode) return currentModel !== String(vehicle.model || "").trim() || Boolean(currentBrandAndModel);
		const expectedBrandAndModel = String(vehicle.brandAndModel || vehicle.brandAndModels?.[0] || "").trim();
		if (expectedBrandAndModel) return currentBrandAndModel !== expectedBrandAndModel;
		const expectedModel = String(vehicle.model || vehicle.modelLabel || "").trim();
		if (expectedModel) return currentModel !== expectedModel && currentModel !== String(vehicle.modelLabel || "").trim();
		return Boolean(currentBrandAndModel || currentModel);
	}
	#bind() {
		this.plateDisplay?.addEventListener("input", () => {
			const clean = stripKenteken(this.plateHidden?.value || this.plateDisplay?.value || "");
			if (!isCompleteKenteken(clean)) {
				this.lookupController?.abort();
				this._lookupPromise = null;
				this.lastLookedUp = "";
				this.#stopCosyLoading();
				this.#setBusy(false);
				if (this.root.getAttribute("data-kenteken-state") === "loading") this.root.removeAttribute("data-kenteken-state");
				if (this.vehicle?.source === "rdw" || this.vehicle?.source === "chassis") this.#resetVehicle({ keepPlate: true });
				this.#setStatus("");
				this.#clearInvalidStroke();
			}
		});
		if (this.variant === "panel" || this.variant === "config" || this.variant === "inline" || this.variant === "filter" || this.variant === "menu") {
			const plateHost = this.root.querySelector("kenteken-input");
			if (plateHost) {
				plateHost.addEventListener("kenteken-input:complete", () => {
					this.#onPlateReady();
				});
				plateHost.addEventListener("kenteken-input:blur", () => {
					this.#onPlateReady();
				});
			} else {
				this.plateDisplay?.addEventListener("input", () => {
					const clean = stripKenteken(this.plateHidden?.value || this.plateDisplay?.value || "");
					if (isCompleteKenteken(clean)) this.#onPlateReady();
				});
				this.plateDisplay?.addEventListener("blur", () => {
					this.#onPlateReady();
				});
			}
		}
		this.root.addEventListener("vp-model-select:change", (event) => {
			const detail = event.detail || {};
			this.#onModelSelect(detail.brand || "", detail.label || detail.value || "", detail.brandAndModel || "", Array.isArray(detail.brandAndModels) ? detail.brandAndModels : [], { navigate: Boolean(detail.viewAll) });
		});
		this.cosyImage?.addEventListener("error", () => {
			const src = this.cosyImage?.getAttribute("src") || "";
			if (!src) return;
			if (this.vehicle?.brandAndModel && isCosyImageUrl(src)) uncacheCosyForModel(this.vehicle.brandAndModel, src);
			const managed = this.#managedCosyUrl(this.vehicle);
			if (managed && managed !== src) {
				this.#setCosyImage(managed, { animate: false });
				return;
			}
			this.cosyImage.removeAttribute("src");
			this.cosyImage.hidden = true;
			this.#showPlaceholder({ animate: false });
		});
		this.clearButton?.addEventListener("click", () => {
			this.#resetVehicle();
		});
		this.categorySelect?.addEventListener("change", () => {
			this.#setStatus("");
		});
		this.form?.addEventListener("submit", (event) => this.#onSubmit(event));
	}
	/**
	* Look up the plate when it is complete (6 chars) or on blur fallback.
	*/
	async #onPlateReady() {
		const clean = stripKenteken(this.plateHidden?.value || this.plateDisplay?.value || "");
		if (!isCompleteKenteken(clean)) {
			if (clean) this.#reportInvalidKenteken();
			return;
		}
		if (clean === this.lastLookedUp && this.vehicle?.kenteken === clean) return;
		if (!await this.#lookupPlate(clean)) return;
		this.#setStatus("");
		if (this.variant === "filter" || this.variant === "menu") this.#navigate();
		if (this.variant === "filter") this.#revealStickyVehicle();
	}
	#restore() {
		const saved = loadVehicle();
		if (!saved) return;
		this.vehicle = saved;
		this.#renderVehicle(saved, {
			persist: false,
			animate: false
		});
		if (saved.source === "model-select" && saved.brandAndModel) this.#refreshModelSelectImage(saved);
		this.#whenControlsReady().then(() => {
			const vehicle = loadVehicle();
			if (!vehicle) return;
			this.#syncControlsFromVehicle(vehicle);
		});
		if (this.variant === "filter") scheduleVehicleFilterRestoreSync(() => this.#syncFiltersToVehicle(saved));
		window.dispatchEvent(new CustomEvent("vp-vehicle:change", { detail: { vehicle: saved } }));
	}
	/**
	* Apply merk + brand_and_model_v2 facets when the URL is missing them (or still
	* uses the obsolete v1 param).
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	*/
	#syncFiltersToVehicle(vehicle) {
		if (this.#isSearchPage()) {
			clearPlpVehiclePending();
			return;
		}
		if (!collectionAllowsVehicleFilters(this.modelCatalog, window.location.pathname, vehicle)) {
			this.#clearVehicleFilters();
			clearPlpVehiclePending();
			return;
		}
		if (!this.#vehicleFiltersNeedSync(vehicle)) {
			clearPlpVehiclePending();
			return;
		}
		const base = vehicleFilterBaseUrl(vehicle, `${window.location.pathname}${window.location.search}`);
		this.#applyFilterUrl(buildVehicleFilterUrl(vehicle, base), { motion: false });
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {boolean}
	*/
	#vehicleFiltersNeedSync(vehicle) {
		return vehicleFiltersNeedSync(vehicle);
	}
	/**
	* Keep multiple kenteken panels in sync when session vehicle changes elsewhere.
	* @param {import('@theme/kenteken-api').VpVehicle|null} vehicle
	*/
	#onExternalVehicleChange(vehicle) {
		if (!vehicle) {
			if (!this.vehicle) return;
			this.vehicle = null;
			this.lastLookedUp = "";
			this.#setCosyImage("");
			this.#syncVehicleChrome(null);
			this.root.removeAttribute("data-has-vehicle");
			this.root.removeAttribute("data-kenteken-match");
			this.root.removeAttribute("data-kenteken-state");
			this.root.removeAttribute("data-fallback");
			this.root.removeAttribute("data-fallback-image");
			if (this.plateDisplay && this.plateHidden) {
				this.plateDisplay.value = "";
				this.plateHidden.value = "";
			}
			if (typeof this.modelSelect?.clearSelection === "function") this.modelSelect.clearSelection({ silent: true });
			return;
		}
		const sameVehicle = this.vehicle?.updatedAt === vehicle.updatedAt && this.vehicle?.kenteken === vehicle.kenteken && this.vehicle?.modelLabel === vehicle.modelLabel;
		const cosyMissing = !this._cosyInFlight && (!this.cosyImage || this.cosyImage.hidden || !this.cosyImage.getAttribute("src") || !this.root.hasAttribute("data-has-cosy"));
		if (sameVehicle && !cosyMissing && !this.#controlsDifferFromVehicle(vehicle)) return;
		this.vehicle = vehicle;
		this.#syncControlsFromVehicle(vehicle);
		const animate = !sameVehicle && this.variant === "config";
		this.#renderVehicle(vehicle, {
			persist: false,
			animate,
			skipControls: true
		});
		this.root.setAttribute("data-kenteken-state", "ready");
	}
	/**
	* Re-resolve chassis cosy after reload so stale series catalog images are not kept.
	*
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	*/
	#refreshModelSelectImage(vehicle) {
		if (!vehicle.brandAndModel) return;
		const imageUrl = this.#resolveModelSelectImageUrl(vehicle.merk || "", vehicle.brandAndModel, vehicle.chassisCode);
		let nextUrl = vehicle.imageUrl;
		if (imageUrl) nextUrl = imageUrl;
		else if (vehicle.imageUrl && !isCosyImageUrl(vehicle.imageUrl)) nextUrl = null;
		else return;
		if (nextUrl === vehicle.imageUrl) return;
		const next = {
			...vehicle,
			imageUrl: nextUrl
		};
		this.vehicle = next;
		this.#renderVehicle(next, {
			persist: true,
			animate: false
		});
	}
	/**
	* @param {string} clean
	* @returns {Promise<boolean>}
	*/
	async #lookupPlate(clean) {
		if (!isCompleteKenteken(clean)) return false;
		if (!this.proxyUrl) {
			this.#reportError(ERROR_MESSAGES.missing_proxy);
			this.root.setAttribute("data-kenteken-state", "error");
			return false;
		}
		if (clean === this.lastLookedUp && this.vehicle?.kenteken === clean) return true;
		if (clean === this.lastLookedUp && this._lookupPromise && this.lookupController && !this.lookupController.signal.aborted) return this._lookupPromise;
		this.lastLookedUp = clean;
		this.lookupController?.abort();
		this.lookupController = new AbortController();
		const { signal } = this.lookupController;
		this.root.setAttribute("data-kenteken-state", "loading");
		this.#setStatus("Voertuig opzoeken…", "loading");
		this.#setBusy(true);
		this.#startCosyLoading();
		const promise = (async () => {
			try {
				const vehicle = await lookupKenteken(this.proxyUrl, clean, { signal });
				if (signal.aborted) return false;
				const withCosy = await this.#withModelCosyFallback(vehicle);
				if (signal.aborted) return false;
				this.vehicle = withCosy;
				this.#renderVehicle(withCosy);
				this.root.setAttribute("data-kenteken-state", "ready");
				this.root.toggleAttribute("data-fallback", withCosy.chassisStatus !== "hit");
				this.#setStatus("");
				return true;
			} catch (error) {
				if (error?.name === "AbortError") return false;
				this.lastLookedUp = "";
				this.#stopCosyLoading();
				const code = error?.code || (error?.message === "Failed to fetch" ? "network" : "upstream_error");
				this.root.setAttribute("data-kenteken-state", "error");
				this.root.removeAttribute("data-fallback");
				if (this.vehicle?.kenteken) this.#resetVehicle({
					keepPlate: true,
					keepStatus: true
				});
				this.#reportError(ERROR_MESSAGES[code] || ERROR_MESSAGES.upstream_error);
				return false;
			} finally {
				this.#setBusy(false);
				if (this._lookupPromise === promise) this._lookupPromise = null;
			}
		})();
		this._lookupPromise = promise;
		return promise;
	}
	/**
	* @param {string} brand
	* @param {string} label
	* @param {string} [brandAndModel]
	* @param {string[]} [brandAndModels]
	* @param {{ navigate?: boolean }} [options]
	*/
	#onModelSelect(brand, label, brandAndModel = "", brandAndModels = [], options = {}) {
		if (!label) {
			this.#resetVehicle();
			return;
		}
		if (this.plateDisplay && this.plateHidden) {
			this.plateDisplay.value = "";
			this.plateHidden.value = "";
			this.lastLookedUp = "";
		}
		const vehicle = vehicleFromModelSelect(brand, label, brandAndModel, brandAndModels);
		const resolvedBrandAndModel = String(brandAndModel || vehicle.brandAndModel || "").trim();
		this.vehicle = vehicle;
		const imageUrl = this.#resolveModelSelectImageUrl(vehicle.merk || brand, resolvedBrandAndModel, vehicle.chassisCode);
		const nextVehicle = imageUrl ? {
			...vehicle,
			imageUrl
		} : vehicle;
		this.vehicle = nextVehicle;
		if (!imageUrl) {
			this.root.setAttribute("data-kenteken-state", "loading");
			this.#startCosyLoading();
		} else this.#stopCosyLoading();
		this.#renderVehicle(nextVehicle, { animate: false });
		this.root.setAttribute("data-kenteken-state", "ready");
		if (this.variant === "filter") {
			this.#navigate();
			this.#revealStickyVehicle();
		} else if (options.navigate && (this.variant === "panel" || this.variant === "menu")) {
			this.#navigate();
			this.#revealStickyVehicle();
		}
	}
	/**
	* Chassis-level cosy for manual model select via `vehicle_generation.cosy_image`.
	* Never falls back to Voertuigserie catalog images.
	*
	* @param {string} brand
	* @param {string} brandAndModel
	* @param {string|null|undefined} chassisCode
	* @returns {string}
	*/
	#resolveModelSelectImageUrl(brand, brandAndModel, chassisCode) {
		return this.#managedCosyUrl({
			brandAndModel,
			chassisCode
		}) || "";
	}
	/**
	* @param {Event} event
	*/
	async #onSubmit(event) {
		event.preventDefault();
		const plate = stripKenteken(this.plateHidden?.value || "");
		const modelValue = this.modelSelect?.querySelector("[data-vp-model-value]")?.value || "";
		const modelBrandAndModel = this.modelSelect?.querySelector("[data-vp-model-brand-and-model]")?.value || "";
		const categoryUrl = this.#categoryUrl();
		if (this.variant === "config") {
			if (!this.vehicle) {
				if (plate && !isCompleteKenteken(plate)) {
					this.#reportInvalidKenteken();
					return;
				}
				if (isCompleteKenteken(plate)) {
					if (!await this.#lookupPlate(plate)) return;
				} else {
					this.#setStatus("Vul een kenteken in om passende accessoires te zien.", "error");
					return;
				}
			}
			if (!categoryUrl) {
				this.#setStatus("Kies een accessoirecategorie.", "error");
				return;
			}
			this.#navigate(categoryUrl);
			return;
		}
		if (this.variant === "inline") {
			if (isCompleteKenteken(plate)) {
				if (!(this.vehicle?.kenteken === plate && this.lastLookedUp === plate)) await this.#lookupPlate(plate);
				return;
			}
			if (plate && !isCompleteKenteken(plate)) {
				this.#reportInvalidKenteken();
				return;
			}
			if (!this.vehicle && modelValue) {
				const brand = this.modelSelect?.querySelector("[data-vp-model-option][aria-selected=\"true\"]")?.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
				this.#onModelSelect(brand, modelValue, modelBrandAndModel);
			}
			return;
		}
		if (this.variant === "filter") {
			if (isCompleteKenteken(plate)) {
				if (!(this.vehicle?.kenteken === plate && this.lastLookedUp === plate)) {
					if (!await this.#lookupPlate(plate)) return;
				}
				this.#navigate();
				this.#revealStickyVehicle();
				return;
			}
			if (plate && !isCompleteKenteken(plate)) {
				this.#reportInvalidKenteken();
				return;
			}
			if (!this.vehicle && modelValue) {
				const brand = this.modelSelect?.querySelector("[data-vp-model-option][aria-selected=\"true\"]")?.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
				this.#onModelSelect(brand, modelValue, modelBrandAndModel);
				return;
			}
			if (this.vehicle) {
				this.#navigate();
				this.#revealStickyVehicle();
				return;
			}
			this.#setStatus("Vul een kenteken in of selecteer een model.", "error");
			return;
		}
		if (isCompleteKenteken(plate)) {
			if (!(this.vehicle?.kenteken === plate && this.lastLookedUp === plate)) {
				if (!await this.#lookupPlate(plate)) return;
			}
			this.#navigate();
			return;
		}
		if (plate && !isCompleteKenteken(plate)) {
			this.#reportInvalidKenteken();
			return;
		}
		if (!this.vehicle && modelValue) {
			const brand = this.modelSelect?.querySelector("[data-vp-model-option][aria-selected=\"true\"]")?.closest("[data-vp-model-group]")?.getAttribute("data-brand") || "";
			await this.#onModelSelect(brand, modelValue, modelBrandAndModel);
		}
		if (!this.vehicle && !modelValue) {
			this.#setStatus(this.modelSelect ? "Vul een kenteken in of selecteer een model." : "Vul een kenteken in.", "error");
			return;
		}
		this.#navigate();
	}
	/**
	* @returns {boolean}
	*/
	#isSearchPage() {
		try {
			return new URL(window.location.href).pathname.includes("/search");
		} catch {
			return false;
		}
	}
	/**
	* Collection URL used when leaving search with a vehicle filter.
	* @returns {string}
	*/
	#vehicleCollectionBase() {
		const configured = String(this.collectionUrl || "").trim();
		if (configured && !configured.startsWith("?")) try {
			const url = new URL(configured, window.location.origin);
			return `${url.pathname}${url.search}`;
		} catch {}
		return "/collections/all";
	}
	/**
	* @param {string} [collectionUrl]
	*/
	#navigate(collectionUrl) {
		if (!this.vehicle) return;
		saveVehicle(this.vehicle);
		if (this.variant === "filter" && !collectionUrl) {
			if (this.#isSearchPage()) {
				window.location.assign(buildVehicleFilterUrl(this.vehicle, this.#vehicleCollectionBase()));
				return;
			}
			if (!collectionAllowsVehicleFilters(this.modelCatalog, window.location.pathname, this.vehicle)) {
				this.#clearVehicleFilters();
				return;
			}
			const base = vehicleFilterBaseUrl(this.vehicle, `${window.location.pathname}${window.location.search}`, { searchToAll: true });
			this.#applyFilterUrl(buildVehicleFilterUrl(this.vehicle, base));
			return;
		}
		const base = collectionUrl || this.#categoryUrl() || this.collectionUrl;
		if (this.variant === "config" || collectionUrl) {
			const brandScoped = {
				...this.vehicle,
				brandAndModel: null,
				brandAndModels: [],
				merk: brandKey(this.vehicle.merk) || this.vehicle.merk
			};
			window.location.assign(buildVehicleFilterUrl(brandScoped, base));
			return;
		}
		window.location.assign(buildVehicleFilterUrl(this.vehicle, base));
	}
	/**
	* Apply a filter URL through facets AJAX when available; otherwise navigate.
	* @param {string} url
	* @param {{ motion?: boolean }} [options]
	*/
	#applyFilterUrl(url, { motion = true } = {}) {
		try {
			const next = new URL(url, window.location.origin);
			if (next.pathname !== window.location.pathname) {
				window.location.assign(`${next.pathname}${next.search}`);
				return;
			}
		} catch {
			window.location.assign(url);
			return;
		}
		const facetsForm = findFacetsFormForPanel(this.root);
		if (facetsForm && typeof facetsForm.updateFiltersByURL === "function") {
			const renderPromise = facetsForm.updateFiltersByURL(url, { motion });
			Promise.resolve(renderPromise).finally(() => {
				if (!motion) clearPlpVehiclePending();
			});
			document.getElementById("filters-drawer")?.close?.();
			return;
		}
		window.location.assign(url);
	}
	/**
	* Remove vehicle-derived facet params while keeping other active filters.
	*/
	#clearVehicleFilters() {
		if (this.variant !== "filter") return;
		const base = `${window.location.pathname}${window.location.search}`;
		const cleared = vehicleFilterClearUrl(base);
		if (cleared) this.#applyFilterUrl(cleared);
	}
	/**
	* @returns {string}
	*/
	#categoryUrl() {
		const option = this.categorySelect?.selectedOptions?.[0];
		if (!option) return "";
		const brand = brandKey(this.vehicle?.merk);
		if (brand === "BMW" && option.dataset.bmwUrl) return option.dataset.bmwUrl;
		if (brand === "MINI" && option.dataset.miniUrl) return option.dataset.miniUrl;
		const value = String(option.value || option.dataset?.value || "").trim();
		if (brand === "MINI" && /\/collections\/bmw-/i.test(value) && option.dataset.miniUrl) return option.dataset.miniUrl;
		if (brand === "BMW" && /\/collections\/mini-/i.test(value) && option.dataset.bmwUrl) return option.dataset.bmwUrl;
		return value;
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle|null} vehicle
	*/
	#syncVehicleChrome(vehicle) {
		const label = vehicle ? this.#displayLabel(vehicle) : "";
		if (this.vehicleLabelEl) this.vehicleLabelEl.textContent = label;
		if (this.clearButton) {
			this.clearButton.hidden = !vehicle;
			this.clearButton.setAttribute("aria-label", vehicle ? `Wis ${label}` : "Wis voertuig");
		}
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @param {{ persist?: boolean, animate?: boolean }} [options]
	*/
	#renderVehicle(vehicle, { persist = true, animate = true, skipControls = false } = {}) {
		const cosyUrl = this.#resolveCosyUrl(vehicle);
		const next = cosyUrl && cosyUrl !== vehicle.imageUrl ? {
			...vehicle,
			imageUrl: cosyUrl
		} : vehicle;
		this.vehicle = next;
		if (persist) saveVehicle(next);
		if (!skipControls) this.#syncControlsFromVehicle(next);
		this.#syncVehicleChrome(next);
		this.#setCosyImage(cosyUrl, { animate });
		this.root.toggleAttribute("data-has-vehicle", Boolean(next));
		this.root.toggleAttribute("data-kenteken-match", Boolean(next?.kenteken && next.source !== "model-select"));
		this.#syncStickyFabMedia(next);
	}
	/**
	* Update sticky FAB cosy image directly so model-select works even when the
	* sticky bundle is still CDN-cached without model-only qualify logic.
	*
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	*/
	#syncStickyFabMedia(vehicle) {
		this.#applyStickyFabMedia(vehicle);
		if (!vehicle || vehicle.kenteken) return;
		for (const delay of [
			50,
			200,
			500
		]) window.setTimeout(() => {
			const current = loadVehicle();
			if (current && !current.kenteken) this.#applyStickyFabMedia(current);
		}, delay);
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	*/
	#applyStickyFabMedia(vehicle) {
		if (!vehicle) return;
		if (!(String(vehicle.kenteken || "").replace(/[^a-zA-Z0-9]/g, "").length >= 6 || !vehicle.kenteken && (vehicle.brandAndModel || vehicle.chassisCode))) return;
		const sticky = document.querySelector("vp-sticky-kenteken");
		if (!sticky) return;
		const url = vehicle.imageUrl || this.#managedCosyUrl(vehicle);
		if (!url) return;
		const media = sticky.querySelector("[data-vp-sticky-kenteken-fab-media]");
		const img = sticky.querySelector("[data-vp-sticky-kenteken-fab-image]");
		if (!(media instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;
		img.src = url.startsWith("//") ? `https:${url}` : url;
		img.referrerPolicy = "no-referrer";
		media.hidden = false;
		sticky.setAttribute("data-has-kenteken", "");
		sticky.removeAttribute("data-fab-placeholder");
	}
	/**
	* If the live Cosy URL fails (500, placeholder, etc.), fall back to the
	* merchant-managed `vehicle_generation.cosy_image`.
	*
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {Promise<import('@theme/kenteken-api').VpVehicle>}
	*/
	async #withModelCosyFallback(vehicle) {
		if (vehicle.imageUrl) try {
			await preloadVehicleImage(vehicle.imageUrl);
			return vehicle;
		} catch {
			if (vehicle.brandAndModel && isCosyImageUrl(vehicle.imageUrl)) uncacheCosyForModel(vehicle.brandAndModel, vehicle.imageUrl);
		}
		const managed = this.#managedCosyUrl(vehicle);
		if (managed) try {
			await preloadVehicleImage(managed);
			return {
				...vehicle,
				imageUrl: managed
			};
		} catch {}
		return {
			...vehicle,
			imageUrl: null
		};
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle | null | undefined} vehicle
	* @returns {string}
	*/
	#managedCosyUrl(vehicle) {
		return managedCosyForVehicle(vehicle, this.cosyImages);
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {string}
	*/
	#collectionImageUrl(vehicle) {
		if (vehicle.brandAndModel) {
			const fromExactModel = this.modelImages[vehicle.brandAndModel];
			if (fromExactModel) return fromExactModel;
		}
		const mapped = dropdownFromVehicle(vehicle);
		if (mapped) {
			const fromMap = this.modelImages[`${mapped.brand}|${mapped.label}`];
			if (fromMap) return fromMap;
		}
		if (vehicle.merk && vehicle.model) {
			const fromSelect = this.modelImages[`${vehicle.merk}|${vehicle.model}`];
			if (fromSelect) return fromSelect;
		}
		return "";
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {string}
	*/
	#resolveCosyUrl(vehicle) {
		return String(vehicle.imageUrl || "").trim() || this.#managedCosyUrl(vehicle);
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @returns {string}
	*/
	#displayLabel(vehicle) {
		return displayLabelForVehicle(vehicle) || "Voertuig gevonden";
	}
	/**
	* @param {string} url
	* @param {{ animate?: boolean }} [options]
	*/
	async #setCosyImage(url, { animate = true } = {}) {
		if (!this.cosyImage) return;
		const resolved = url || "";
		const current = this.cosyImage.getAttribute("src") || "";
		const token = this._cosyToken = (this._cosyToken || 0) + 1;
		const currentSrc = normalizeImageUrl(current);
		this.#killCosyTween({ restoreVisible: Boolean(currentSrc && resolved && currentSrc === normalizeImageUrl(resolved)) });
		this._cosyInFlight = Boolean(resolved);
		const managed = this.#managedCosyUrl(this.vehicle);
		/** @type {string[]} */
		const candidates = [];
		const addCandidate = (candidate) => {
			const value = normalizeImageUrl(candidate);
			if (value && !candidates.includes(value)) candidates.push(value);
		};
		addCandidate(resolved);
		addCandidate(managed);
		if (!candidates.length) {
			this._cosyInFlight = false;
			this.root.removeAttribute("data-fallback-image");
			await this.#showPlaceholder({ animate });
			return;
		}
		if (candidates.length === 1 && currentSrc === candidates[0] && this.root.hasAttribute("data-has-cosy")) {
			this._cosyInFlight = false;
			this.#stopCosyLoading();
			this.root.toggleAttribute("data-fallback-image", !isVehicleCosyImageUrl(candidates[0], this.cosyImages));
			this.#applyMediaLayer("cosy");
			if (!this.root.hasAttribute("data-cosy-fit")) this.#applyCosyFit(candidates[0]);
			return;
		}
		if (currentSrc && !candidates.includes(currentSrc) && !this.root.classList.contains("is-cosy-loading")) this.#applyMediaLayer("placeholder");
		let finalUrl = "";
		for (const candidate of candidates) try {
			await preloadVehicleImage(candidate);
			finalUrl = candidate;
			break;
		} catch {
			if (isCosyImageUrl(candidate) && this.vehicle?.brandAndModel) uncacheCosyForModel(this.vehicle.brandAndModel, candidate);
		}
		if (token !== this._cosyToken) return;
		if (!finalUrl) {
			this._cosyInFlight = false;
			if (this.vehicle?.imageUrl && isCosyImageUrl(this.vehicle.imageUrl)) {
				this.vehicle = {
					...this.vehicle,
					imageUrl: null
				};
				saveVehicle(this.vehicle);
			}
			this.root.removeAttribute("data-fallback-image");
			await this.#showPlaceholder({ animate });
			return;
		}
		if (isCosyImageUrl(finalUrl) && this.vehicle?.brandAndModel) cacheCosyForModel(this.vehicle.brandAndModel, finalUrl);
		this.root.toggleAttribute("data-fallback-image", !isVehicleCosyImageUrl(finalUrl, this.cosyImages));
		this.cosyImage.alt = this.vehicle?.modelLabel || this.#displayLabel(this.vehicle || {}) || "Uw voertuig";
		this.cosyImage.referrerPolicy = "no-referrer";
		this.cosyImage.loading = "eager";
		this.cosyImage.removeAttribute("hidden");
		this.cosyImage.src = normalizeImageUrl(finalUrl);
		await this.#showCosy({ animate: false });
		if (token !== this._cosyToken) return;
		await this.#applyCosyFit(finalUrl);
		if (this.root.hasAttribute("data-cosy-fit")) this.root.removeAttribute("data-fallback-image");
		if (token === this._cosyToken) this._cosyInFlight = false;
	}
	/**
	* Measure the car's opaque bbox in the (already preloaded) Cosy render and
	* scale it to the silhouette width via CSS vars. Config sections use a safe
	* contain fallback when measurement fails (no CORS / no alpha / decode error).
	* @param {string} url
	*/
	async #applyCosyFit(url) {
		const fit = await computeCosyFit(normalizeImageUrl(url), {
			boxAspect: COSY_FIT_BOX_ASPECT,
			maxHeight: COSY_FIT_MAX_HEIGHT
		});
		if (!fit) {
			this.#clearCosyFit();
			if (this.variant === "config" && isVehicleCosyImageUrl(url, this.cosyImages)) this.root.setAttribute("data-cosy-fit-fallback", "");
			return;
		}
		this.root.removeAttribute("data-cosy-fit-fallback");
		this.root.style.setProperty("--kenteken-cosy-scale", String(fit.scale));
		this.root.style.setProperty("--kenteken-cosy-tx", `${fit.txPct}%`);
		this.root.style.setProperty("--kenteken-cosy-ty", `${fit.tyPct}%`);
		this.root.setAttribute("data-cosy-fit", "");
	}
	#clearCosyFit() {
		this.root.removeAttribute("data-cosy-fit");
		this.root.removeAttribute("data-cosy-fit-fallback");
		this.root.style.removeProperty("--kenteken-cosy-scale");
		this.root.style.removeProperty("--kenteken-cosy-tx");
		this.root.style.removeProperty("--kenteken-cosy-ty");
	}
	/**
	* Kill an in-flight cosy reveal. GSAP timeline.kill() skips onComplete, so without
	* restore the img can stay at autoAlpha:0 forever (common when panels sync).
	* @param {{ restoreVisible?: boolean }} [options]
	*/
	#killCosyTween({ restoreVisible = false } = {}) {
		this._cosyTween?.kill?.();
		this._cosyTween = null;
		if (!restoreVisible || !this.cosyImage) return;
		if (!this.cosyImage.getAttribute("src")) return;
		this.#applyMediaLayer("cosy");
	}
	/**
	* Pool-blue “scan” dash racing along the car silhouette while Chassis/RDW resolves
	* and the cosy image preloads. Survives past `#setBusy(false)` until the photo
	* actually swaps in.
	*/
	/**
	* Show exactly one media layer — cosy photo or SVG silhouette, never both.
	* @param {'cosy' | 'placeholder'} mode
	*/
	#applyMediaLayer(mode) {
		if (!this.cosyImage) return;
		this._cosyTween?.kill?.();
		this._cosyTween = null;
		const gsap = this.gsap;
		const clearInline = (el) => {
			if (!el) return;
			el.style.opacity = "";
			el.style.visibility = "";
			el.style.transform = "";
		};
		if (mode === "cosy") {
			this.cosyImage.hidden = false;
			clearInline(this.cosyImage);
			clearInline(this.placeholder);
			this.placeholder?.setAttribute("hidden", "");
			this.root.setAttribute("data-has-cosy", "");
			gsap?.set(this.cosyImage, { clearProps: "opacity,visibility,transform,width,height,autoAlpha" });
			gsap?.set(this.placeholder, { clearProps: "opacity,visibility,transform,autoAlpha" });
			return;
		}
		this.cosyImage.hidden = true;
		clearInline(this.cosyImage);
		clearInline(this.placeholder);
		this.placeholder?.removeAttribute("hidden");
		this.root.removeAttribute("data-has-cosy");
		gsap?.set(this.cosyImage, { clearProps: "opacity,visibility,transform,width,height,autoAlpha" });
		gsap?.set(this.placeholder, { clearProps: "opacity,visibility,transform,autoAlpha" });
	}
	async #startCosyLoading() {
		if (!this.placeholder) return;
		this._loadingTween?.kill?.();
		this._loadingTween = null;
		this.#applyMediaLayer("placeholder");
		this.root.classList.add("is-cosy-loading");
		const path = this.placeholder.querySelector("[data-kenteken-outline]");
		if (!path || typeof path.getTotalLength !== "function") return;
		if (REDUCED()) return;
		const gsap = this.gsap || await ensureGsap();
		this.gsap = gsap;
		if (!gsap || !this.root.classList.contains("is-cosy-loading")) return;
		let tracer = this.placeholder.querySelector("[data-kenteken-tracer]");
		if (!tracer) {
			tracer = path.cloneNode();
			tracer.setAttribute("data-kenteken-tracer", "");
			tracer.removeAttribute("data-kenteken-outline");
			tracer.removeAttribute("id");
			path.after(tracer);
		}
		const length = path.getTotalLength();
		if (!length) return;
		const dash = Math.max(48, length * .16);
		gsap.set(tracer, {
			strokeDasharray: `${dash} ${length}`,
			strokeDashoffset: 0,
			attr: { "stroke-width": Number(path.getAttribute("stroke-width")) || .75 }
		});
		this._loadingTween = gsap.to(tracer, {
			strokeDashoffset: -length,
			duration: 1.55,
			ease: "none",
			repeat: -1
		});
	}
	#removeLoadingTracer() {
		const tracer = this.placeholder?.querySelector("[data-kenteken-tracer]");
		if (!tracer) return;
		this.gsap?.set(tracer, { clearProps: "strokeDasharray,strokeDashoffset" });
		tracer.remove();
	}
	#stopCosyLoading() {
		this._loadingTween?.kill?.();
		this._loadingTween = null;
		this.root.classList.remove("is-cosy-loading");
		this.#removeLoadingTracer();
	}
	/**
	* @param {{ animate?: boolean }} [options]
	*/
	async #showCosy(_options = {}) {
		if (!this.cosyImage) return;
		this.#stopCosyLoading();
		this.#killCosyTween({ restoreVisible: false });
		this.#applyMediaLayer("cosy");
	}
	/**
	* @param {{ animate?: boolean }} [options]
	*/
	async #showPlaceholder({ animate = true } = {}) {
		if (!this.cosyImage) return;
		this.#stopCosyLoading();
		this.#killCosyTween({ restoreVisible: false });
		this._cosyInFlight = false;
		const gsap = this.gsap || await ensureGsap();
		this.gsap = gsap;
		const hadCosy = Boolean(this.cosyImage.getAttribute("src")) && !this.cosyImage.hidden;
		if (!animate || REDUCED() || !gsap || !hadCosy) {
			this.cosyImage.removeAttribute("src");
			this.#clearCosyFit();
			this.#applyMediaLayer("placeholder");
			return;
		}
		this.placeholder?.removeAttribute("hidden");
		gsap.set(this.placeholder, {
			autoAlpha: 0,
			scale: .94,
			x: -12
		});
		gsap.set(this.cosyImage, {
			autoAlpha: 1,
			scale: 1,
			x: 0
		});
		this._cosyTween = gsap.timeline({
			defaults: { ease: "power2.inOut" },
			onComplete: () => {
				this.cosyImage.removeAttribute("src");
				this.#clearCosyFit();
				this.#applyMediaLayer("placeholder");
			}
		}).to(this.cosyImage, {
			autoAlpha: 0,
			scale: .9,
			x: 20,
			duration: .3
		}, 0).to(this.placeholder, {
			autoAlpha: 1,
			scale: 1,
			x: 0,
			duration: .4
		}, .05);
	}
	/**
	* @param {{ keepPlate?: boolean, keepStatus?: boolean }} [options]
	*/
	#resetVehicle({ keepPlate = false, keepStatus = false } = {}) {
		const hadVehicle = Boolean(this.vehicle);
		this.vehicle = null;
		this.lastLookedUp = "";
		clearVehicle();
		this.#setCosyImage("");
		this.#syncVehicleChrome(null);
		this.root.removeAttribute("data-has-vehicle");
		this.root.removeAttribute("data-kenteken-match");
		this.root.removeAttribute("data-kenteken-state");
		this.root.removeAttribute("data-fallback");
		this.root.removeAttribute("data-fallback-image");
		if (!keepStatus) this.#setStatus("");
		if (!keepPlate && this.plateDisplay && this.plateHidden) {
			this.plateDisplay.value = "";
			this.plateHidden.value = "";
		}
		if (typeof this.modelSelect?.clearSelection === "function") this.modelSelect.clearSelection({ silent: true });
		if (this.variant === "filter" && hadVehicle && !keepPlate) this.#clearVehicleFilters();
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	*/
	#syncModelSelectFromVehicle(vehicle) {
		if (!this.modelSelect || typeof this.modelSelect.setSelection !== "function") return;
		if (vehicle.source === "model-select" && Array.isArray(vehicle.brandAndModels) && vehicle.brandAndModels.length > 1 && !vehicle.chassisCode && vehicle.model) {
			this.modelSelect.setSelection({
				value: vehicle.model,
				label: vehicle.model,
				brand: vehicle.merk || "",
				silent: true
			});
			return;
		}
		const brandAndModel = String(vehicle.brandAndModel || "").trim() || (Array.isArray(vehicle.brandAndModels) ? String(vehicle.brandAndModels.find((model) => String(model || "").trim()) || "").trim() : "");
		if (brandAndModel && isKnownBrandAndModel(brandAndModel)) {
			if (this.modelSelect.setSelection({
				brand: vehicle.merk || "",
				brandAndModel,
				silent: true
			})) return;
		}
		const mapped = dropdownFromVehicle(vehicle);
		if (mapped) {
			this.modelSelect.setSelection({
				value: mapped.label,
				label: mapped.label,
				brand: mapped.brand,
				brandAndModel: mapped.brandAndModel || brandAndModel,
				silent: true
			});
			return;
		}
		if (brandAndModel) {
			if (vehicle.merk) {
				const label = String(vehicle.model || vehicle.modelLabel || "").trim();
				if (label) {
					this.modelSelect.setSelection({
						value: vehicle.model || label,
						label: vehicle.modelLabel || label,
						brand: vehicle.merk,
						brandAndModel,
						silent: true
					});
					return;
				}
			}
		}
		if (vehicle.source === "model-select" && vehicle.model) {
			this.modelSelect.setSelection({
				value: vehicle.model,
				label: vehicle.model,
				brand: vehicle.merk || "",
				brandAndModel,
				silent: true
			});
			return;
		}
		if (vehicle.modelLabel || vehicle.model) {
			this.modelSelect.setSelection({
				value: vehicle.model || vehicle.modelLabel,
				label: vehicle.modelLabel || vehicle.model,
				brand: vehicle.merk || "",
				brandAndModel,
				silent: true
			});
			return;
		}
		if (!brandAndModel) this.modelSelect.clearSelection({ silent: true });
	}
	/**
	* On PLP filter panels, briefly open the sticky kenteken so the customer
	* sees their vehicle moved to the FAB. Desktop sidebar hides via CSS.
	*/
	#revealStickyVehicle() {
		if (this.variant !== "filter") return;
		if (!this.root.closest(".vp-filters-sidebar__kenteken")) return;
		const sticky = document.querySelector("vp-sticky-kenteken");
		if (!sticky || typeof sticky.revealVehicle !== "function") return;
		if (sticky.isOpen) return;
		sticky.revealVehicle({ holdMs: 2200 });
	}
	/**
	* @param {string} message
	* @param {'loading'|'success'|'error'|''} [tone]
	*/
	#setStatus(message, tone = "") {
		if (!this.statusEl) return;
		this.statusEl.textContent = message || "";
		if (tone) this.statusEl.dataset.tone = tone;
		else delete this.statusEl.dataset.tone;
		this.statusEl.hidden = !message;
		this.root.classList.toggle("has-status", Boolean(message));
		if (tone !== "error") this.#clearInvalidStroke();
	}
	#reportInvalidKenteken() {
		this.#reportError(ERROR_MESSAGES.invalid_kenteken);
	}
	/**
	* Show an error message and blink the silhouette outline red.
	* @param {string} message
	*/
	#reportError(message) {
		this.#setStatus(message, "error");
		this.#blinkInvalidStroke();
	}
	#clearInvalidStroke() {
		this._invalidStrokeTween?.kill?.();
		this._invalidStrokeTween = null;
		if (!this.placeholder) return;
		this.gsap?.set(this.placeholder, { clearProps: "color" });
		this.placeholder.style.removeProperty("color");
	}
	/**
	* Blink the silhouette stroke red a few times on lookup/validation errors.
	*/
	async #blinkInvalidStroke() {
		this.#stopCosyLoading();
		this.#applyMediaLayer("placeholder");
		if (!this.placeholder) return;
		if (REDUCED()) {
			this.placeholder.style.color = "#d92b2b";
			return;
		}
		const gsap = this.gsap || await ensureGsap();
		this.gsap = gsap;
		if (!gsap || !this.placeholder) return;
		this._invalidStrokeTween?.kill?.();
		const idleStroke = getComputedStyle(this.placeholder).getPropertyValue("color")?.trim() || "#f7f7f7";
		gsap.set(this.placeholder, { color: idleStroke });
		this._invalidStrokeTween = gsap.timeline({ onComplete: () => {
			gsap.set(this.placeholder, { clearProps: "color" });
			this.placeholder?.style.removeProperty("color");
			this._invalidStrokeTween = null;
		} });
		for (let i = 0; i < 3; i += 1) this._invalidStrokeTween.to(this.placeholder, {
			color: "#d92b2b",
			duration: .55,
			ease: "power1.inOut"
		}).to(this.placeholder, {
			color: idleStroke,
			duration: .55,
			ease: "power1.inOut"
		});
	}
	/**
	* @param {boolean} busy
	*/
	#setBusy(busy) {
		this.submitButton?.toggleAttribute("aria-busy", busy);
		this.root.classList.toggle("is-loading", busy);
	}
};
/**
* Normalize RDW/Chassis brand strings to BMW | MINI.
* @param {string|null|undefined} merk
* @returns {'BMW'|'MINI'|''}
*/
function brandKey(merk) {
	const value = String(merk || "").trim().toUpperCase();
	if (value === "BMW" || value.startsWith("BMW ")) return "BMW";
	if (value === "MINI" || value.startsWith("MINI ") || value === "MINI.") return "MINI";
	return "";
}
/**
* @param {HTMLElement} root
* @returns {Record<string, string>}
*/
function readModelImages(root) {
	return parseJsonObject(root.querySelector("[data-kenteken-model-images]")?.textContent);
}
function readCosyImages(root) {
	return parseJsonObject(root.querySelector("[data-kenteken-cosy-images]")?.textContent);
}
/**
* Parse a JSON object out of a Liquid script tag. `{% paginate %}` and
* Liquid errors can wrap or inject text around the object — take the first
* `{` … last `}` so a recoverable payload still loads.
*
* @param {string|null|undefined} text
* @returns {Record<string, string>}
*/
function parseJsonObject(text) {
	const raw = String(text || "");
	const start = raw.indexOf("{");
	const end = raw.lastIndexOf("}");
	if (start < 0 || end <= start) return {};
	try {
		const parsed = JSON.parse(raw.slice(start, end + 1));
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
	} catch {
		return {};
	}
}
/**
* @param {string} url
* @returns {string}
*/
function normalizeImageUrl(url) {
	const value = String(url || "").trim();
	if (!value) return "";
	if (value.startsWith("//")) return `https:${value}`;
	return value;
}
/**
* @param {string} url
* @returns {Promise<void>}
*/
function preloadImage(url) {
	const src = normalizeImageUrl(url);
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.referrerPolicy = "no-referrer";
		img.onload = () => {
			if (img.naturalWidth < 64 || img.naturalHeight < 64) {
				reject(/* @__PURE__ */ new Error("image_too_small"));
				return;
			}
			resolve();
		};
		img.onerror = () => reject(/* @__PURE__ */ new Error("image_error"));
		img.src = src;
	});
}
async function preloadVehicleImage(url) {
	await preloadImage(url);
	if (isCosyImageUrl(url) && !await isUsableCosyImage(url)) throw new Error("unusable_cosy_image");
}
function initKentekenPanels() {
	document.querySelectorAll("[data-kenteken-panel]").forEach((root) => {
		if (!(root instanceof HTMLElement)) return;
		if (root.dataset.kentekenReady) return;
		root.dataset.kentekenReady = "true";
		new KentekenPanel(root);
	});
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initKentekenPanels, { once: true });
else initKentekenPanels();
document.addEventListener("shopify:section:load", initKentekenPanels);
//#endregion
export { KentekenPanel, findFacetsFormForPanel, initKentekenPanels, resetVehicleFilterRestoreSyncForTests, scheduleVehicleFilterRestoreSync };
